import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface ModuleState {
  tableColumns: any[];
  filters: any;
  dashboard?: any;
}

@Injectable({
  providedIn: 'root'
})
export class StateManagementService {

  private state: { [key: string]: ModuleState } = {};
  private adminApiUrl = `${environment.administration}`;
  userId: string | null;

  constructor(private _http: HttpClient) {
    this.userId = localStorage.getItem('userId');
    if (this.userId) {
      this.getUserState(this.userId).subscribe(
        (userState) => {
          this.state = userState || {};
        },
        (error) => {
          console.error('Failed to fetch user state:', error);
          this.state = {};
        }
      );
    }
  }

  setTableColumns(module: string, columns: any[]): void {
    if (!this.state[module]) {
      this.state[module] = { tableColumns: [], filters: {} };
    }
    this.state[module].tableColumns = columns;
    this.saveUserState(this.userId, this.serializePreference()).subscribe();
  }

  setFilters(module: string, filters: any): void {
    if (!this.state[module]) {
      this.state[module] = { tableColumns: [], filters: {} };
    }
    this.state[module].filters = filters;
    this.saveUserState(this.userId, this.serializePreference()).subscribe();
  }

  setDashboardFilters(module: string, dashboard: any): void {
    if (!this.state[module]) {
      this.state[module] = { tableColumns: [], filters: {}, dashboard: {} };
    }
    this.state[module].dashboard = dashboard;
    this.saveUserState(this.userId, this.serializePreference()).subscribe();
  }

  getTableColumns(module: string): any[] | null {
    const columns = this.state[module]?.tableColumns || [];
    return columns.length > 0 ? columns : null;
  }

  getFilters(module: string): any {
    const filters = this.state[module]?.filters || {};
    return Object.keys(filters).length > 0 ? filters : null;
  }

  getDashboardFilters(module: string): any {
    const dashboard = this.state[module]?.dashboard || {};
    return Object.keys(dashboard).length > 0 ? dashboard : null;
  }

  serializePreference(): any {
    return { ...this.state };
  }

  saveUserState(userId: string | null, preference: any): Observable<any> {
    if (!userId) {
      console.error('User ID is null. Cannot save state.');
      return of(null);
    }
    return this._http.put(`${this.adminApiUrl}/userpreferences/${userId}`, preference).pipe(
      catchError((error) => {
        console.error('Error saving user state:', error);
        return of(null);
      })
    );
  }

  getUserState(userId: string): Observable<any> {
    return this._http.get(`${this.adminApiUrl}/userpreferences/${userId}`).pipe(
      map((response: any) => response || {}),
      catchError((error) => {
        console.error('Error fetching user state:', error);
        return of({});
      })
    );
  }

  mergeManageColumnsStates(storedState: any[] | null, uiState: any[]): any[] {
    return (storedState || []).map((storedColumn) => {
      const uiColumn = uiState.find((uiCol) => uiCol.key === storedColumn.key);
      if (uiColumn) {
        return {
          ...uiColumn,
          key: storedColumn.key,
          colName: storedColumn.colName,
          showColumn: uiColumn.isDisabled ? true: storedColumn.showColumn,
        };
      }
      return storedColumn;
    });
  }
}